/**
 * Organization Service
 * Handles organization-related API calls
 */

import http from './http'

export interface Organization {
  id: string
  name: string
  industry?: string
  size?: string
  linkedin_page?: string
  logo_url?: string | null
  created_at: string
  updated_at: string
}

export interface LogoUploadResponse {
  logo_url: string
  uploaded_at: string
  file_size: number
  file_type: string
}

export interface Department {
  id: string
  organization_id: string
  name: string
  description?: string
  created_at: string
}

class OrganizationService {
  /**
   * GET /api/b2b/organizations/me
   * Get current user's organization
   * Returns null if user is not part of an organization (404)
   */
  async getOrganization(): Promise<Organization | null> {
    console.log('[OrganizationService.getOrganization] Fetching organization')
    try {
      const response = await http.get<Organization>('/api/b2b/organizations/me')
      console.log('[OrganizationService.getOrganization] Success:', response.data)
      return response.data
    } catch (error: any) {
      // 404 means user is not part of an organization - this is expected for new users
      if (error.response?.status === 404) {
        console.log('[OrganizationService.getOrganization] User is not part of an organization (expected)')
        return null
      }
      console.error('[OrganizationService.getOrganization] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * PUT /api/b2b/organizations/me
   * Update current user's organization
   */
  async updateOrganization(data: Partial<Organization>): Promise<Organization> {
    console.log('[OrganizationService.updateOrganization] Updating organization:', data)
    try {
      const response = await http.put<Organization>('/api/b2b/organizations/me', data)
      console.log('[OrganizationService.updateOrganization] Success')
      return response.data
    } catch (error: any) {
      console.error('[OrganizationService.updateOrganization] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * POST /api/b2b/organizations/me/logo
   * Upload company logo
   */
  async uploadLogo(file: File): Promise<LogoUploadResponse> {
    console.log('[OrganizationService.uploadLogo] Uploading logo:', file.name, file.type, file.size)

    const formData = new FormData()
    // Append file with field name 'logo' to match backend expectation: logo: UploadFile = File(...)
    formData.append('logo', file, file.name)

    try {
      // Don't set Content-Type header - axios will automatically set it with boundary for FormData
      // The default 'application/json' Content-Type from http service will be overridden by axios
      // when it detects FormData, setting it to 'multipart/form-data' with the proper boundary
      const response = await http.post<LogoUploadResponse>(
        '/api/b2b/organizations/me/logo',
        formData
      )
      console.log('[OrganizationService.uploadLogo] Success:', response.data.logo_url)
      return response.data
    } catch (error: any) {
      // Extract detailed error message from backend response
      const errorMessage = this.handleError(error)
      console.error('[OrganizationService.uploadLogo] Failed:', errorMessage)
      console.error('[OrganizationService.uploadLogo] Full error:', error)
      if (error.response?.data) {
        console.error('[OrganizationService.uploadLogo] Backend error details:', error.response.data)
      }
      throw errorMessage
    }
  }

  /**
   * DELETE /api/b2b/organizations/me/logo
   * Delete company logo
   */
  async deleteLogo(): Promise<void> {
    console.log('[OrganizationService.deleteLogo] Deleting logo')
    try {
      await http.delete('/api/b2b/organizations/me/logo')
      console.log('[OrganizationService.deleteLogo] Success')
    } catch (error: any) {
      console.error('[OrganizationService.deleteLogo] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * GET /api/b2b/organizations/me/logo
   * Get current logo URL
   */
  async getLogo(): Promise<{ logo_url: string }> {
    console.log('[OrganizationService.getLogo] Fetching logo URL')
    try {
      const response = await http.get<{ logo_url: string }>('/api/b2b/organizations/me/logo')
      console.log('[OrganizationService.getLogo] Success:', response.data.logo_url)
      return response.data
    } catch (error: any) {
      console.error('[OrganizationService.getLogo] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * GET /api/b2b/organizations/me/departments
   * Get all departments in the organization
   * Returns empty array if user is not part of an organization (404)
   */
  async getDepartments(): Promise<Department[]> {
    console.log('[OrganizationService.getDepartments] Fetching departments')
    try {
      const response = await http.get<Department[]>('/api/b2b/organizations/me/departments')
      console.log('[OrganizationService.getDepartments] Success:', response.data.length, 'departments')
      return response.data
    } catch (error: any) {
      // 404 means user is not part of an organization - return empty array
      if (error.response?.status === 404) {
        console.log('[OrganizationService.getDepartments] User is not part of an organization (expected), returning empty array')
        return []
      }
      console.error('[OrganizationService.getDepartments] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * POST /api/b2b/organizations/me/departments
   * Create a new department in the organization
   */
  async createDepartment(name: string, description?: string): Promise<Department> {
    console.log('[OrganizationService.createDepartment] Creating department:', name)
    try {
      const response = await http.post<Department>('/api/b2b/organizations/me/departments', {
        name: name.trim(),
        description: description?.trim() || null
      })
      console.log('[OrganizationService.createDepartment] Success:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[OrganizationService.createDepartment] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * PUT /api/b2b/organizations/me/departments/{department_id}
   * Update a department in the organization
   */
  async updateDepartment(departmentId: string, name: string, description?: string): Promise<Department> {
    console.log('[OrganizationService.updateDepartment] Updating department:', departmentId, name)
    try {
      const response = await http.put<Department>(`/api/b2b/organizations/me/departments/${departmentId}`, {
        name: name.trim(),
        description: description?.trim() || null
      })
      console.log('[OrganizationService.updateDepartment] Success:', response.data)
      return response.data
    } catch (error: any) {
      console.error('[OrganizationService.updateDepartment] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * DELETE /api/b2b/organizations/me/departments/{department_id}
   * Delete a department from the organization
   */
  async deleteDepartment(departmentId: string): Promise<void> {
    console.log('[OrganizationService.deleteDepartment] Deleting department:', departmentId)
    try {
      await http.delete(`/api/b2b/organizations/me/departments/${departmentId}`)
      console.log('[OrganizationService.deleteDepartment] Success')
    } catch (error: any) {
      console.error('[OrganizationService.deleteDepartment] Failed:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Error handler - Extracts meaningful error messages from backend responses
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error
      const detail = error.response.data?.detail
      let message = 'An error occurred'
      
      // Handle different error formats from FastAPI
      if (typeof detail === 'string') {
        message = detail
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Validation errors: [{"type":"...","loc":[...],"msg":"..."}]
        message = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ')
      } else if (detail) {
        message = JSON.stringify(detail)
      } else {
        message = error.response.data?.message || error.response.data?.error || message
      }
      
      const customError = new Error(message)
      ;(customError as any).status = error.response.status
      ;(customError as any).data = error.response.data
      return customError
    } else if (error.request) {
      // Request made but no response
      return new Error('Network error. Please check your connection.')
    } else {
      // Something else happened
      return new Error(error.message || 'An unexpected error occurred')
    }
  }
}

export default new OrganizationService()
